import React, { useEffect, useRef, useState } from "react";
import { Box, Text, IconButton, ActionList, ActionMenu } from "@primer/react";
import { PencilIcon, MailIcon, DeviceMobileIcon } from "@primer/octicons-react";
import { phuHuynhApi } from "../../api/phuHuynhApi";
import { useCommonContext } from "../../contexts/common";
import clsx from "clsx";
import UserAvatar from "../user-avatar";
import {
  KebabHorizontalIcon,
  PersonIcon,
  ChevronRightIcon,
  TrashIcon,
  BookIcon,
  TasklistIcon,
} from "@primer/octicons-react";

interface PhuHuynhInfoProps {
  id: number;
  isSelected: boolean;
  onSelected: () => void;
}

interface PhuHuynhData {
  id: number;
  ho_ten: string;
  email: string;
  dien_thoai: string;
  quan_he?: string;
}

const PhuHuynhInfo: React.FC<PhuHuynhInfoProps> = ({
  id,
  isSelected,
  onSelected,
}) => {
  const [formData, setFormData] = useState<PhuHuynhData | null>(null);
  const { translate } = useCommonContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const componentDidMount = useRef<boolean>(false);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    componentDidMount.current = true;
    handleReloadData();
    return () => {
      componentDidMount.current = false;
    };
  }, [id]);

  const handleReloadData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await phuHuynhApi.getById(id);
      if (componentDidMount.current) {
        setIsLoading(false);
        if (res.is_success) {
          setFormData(res.data);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error loading phu huynh details:", error);
    }
  };

  if (isLoading || !formData) return null;

  const contactSx = { display: "flex", alignItems: "center", gap: 2, fontSize: 1, color: "fg.default", minWidth: 0 };

  return (
    <Box
      onClick={isSelected ? undefined : onSelected}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: isSelected ? "accent.muted" : "border.default",
        borderRadius: 2,
        bg: isSelected ? "accent.subtle" : "canvas.default",
        cursor: isSelected ? "default" : "pointer",
        "&:hover": isSelected ? undefined : { borderColor: "accent.muted" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <UserAvatar fullName={formData.ho_ten} size="xlarge" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text as="div" sx={{ fontWeight: "bold", fontSize: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {formData.ho_ten}
          </Text>
          {formData.quan_he && (
            <Text as="div" sx={{ fontSize: 0, color: "fg.muted" }}>{formData.quan_he}</Text>
          )}
        </Box>
      </Box>

      {(formData.dien_thoai || formData.email) && (
        <Box sx={{ display: "grid", gap: 1, mt: 3 }}>
          {formData.dien_thoai && (
            <Box as="a" href={`tel:${formData.dien_thoai}`} sx={{ ...contactSx, textDecoration: "none" }}>
              <Box sx={{ color: "fg.muted", display: "flex" }}><DeviceMobileIcon size={14} /></Box>
              {formData.dien_thoai}
            </Box>
          )}
          {formData.email && (
            <Box sx={contactSx} title={formData.email}>
              <Box sx={{ color: "fg.muted", display: "flex" }}><MailIcon size={14} /></Box>
              <Box as="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formData.email}</Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PhuHuynhInfo;